import React, { useEffect, useState } from 'react'
import { getFamilyMember, put_FamilyMember } from '../../services/FamilyMember';
import { useNavigate, useParams } from 'react-router-dom';
import { FileDrop } from '../../components/FileDrop';
import EmailAlreadyExistChecking from '../../components/EmailAlreadyExistChecking';
import { getFamilyMemberEmailChecking } from '../../services/EmailChecking';
import PasswordValidation from '../../components/PasswordValidation';
import UsernameValidatedComponent from '../../components/UsernameValidatedComponent';
import { getFamilyMemberUsername } from '../../services/UsernameChecking';
import { formatUSPhone, handleUSPhoneChange } from '../../utils/phoneFormatter';
import { useUSPhoneValidation } from '../../hook/useUSPhoneValidation';
import CustomFormatDatePicker from '../../components/CustomFormatDatePicker';
import Cookies from 'js-cookie';
import useConfirm from '../../hook/useConfirm';

const EditFamilyMember = () => {
  const token = Cookies.get("token-my-cdir");
  const navigate = useNavigate();
  const { id }: any = useParams();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [userData, setUserData] = useState<any>({
    id: 0,
    firstName: "",
    lastName: "",
    userName: "",
    emailAddress: "",
    phoneNumber: "",
    birthday: "",
    filePath: "",
    memberId: 0,
    memberRoleId: 0,
    isEnabled: true,
    relationToMember: "",
    imageSize: 0,
    createdAt: "",
    createdBy: "",
    uploadedAt: "",
    uploadedBy: ""
  });
  const [password, setPassword] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
  const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
  const {
    isValid: isPhoneValid,
    touched: phoneTouched,
    setTouched: setPhoneTouched
  } = useUSPhoneValidation(userData.phoneNumber);
  const [isUsernameChecking, setIsUsernameChecking] = useState<boolean>(false);
  const [isEmailChecking, setIsEmailChecking] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      getFamilyMember(id, token!.toString())
        .then(res => {
          setUserData({
            ...res,
            birthday: res?.birthday
              ? res.birthday.split("T")[0]
              : "",
          });
        })
        .catch(error => console.error(error));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const closeModal = () => navigate(-1);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isUsernameChecking || isEmailChecking) return;

    if (!isProcessing) {
      setIsProcessing(true);

      put_FamilyMember({
        ...userData,
        password
      }, token!.toString())
        .then(() => {
          setIsProcessing(false);
          navigate(-1);
        })
        .catch(error => {
          const errorMessage = error?.response?.data || error?.message || "Something went wrong, Please try again.";
          setConfirmData({
            title: 'Warning',
            summary: errorMessage,
            confirmBtn: 'Ok',
            isConfirm: (callback: any) => {
              callback();
            }
          });
          setConfirmPopup(true);
          setIsProcessing(false);
          console.error(error);
        });
    }
  }

  return (
    <div className="w-full">
      <div className="bg-gradient-to-r from-cyan-300 to-blue-300 w-full h-60" />
      <div className="-mt-28 mb-6 px-4">
        <div className="mx-auto max-w-6xl shadow-lg py-8 px-6 relative bg-white rounded">
          <h2 className="text-xl text-secondary font-bold">Edit Family Member</h2>
          <form onSubmit={handleSubmit} className="mt-8 grid xm:grid-cols-2 gap-6 text-secondary">
            <div>
              <label className="font-semibold text-sm">First Name</label>
              <input type='text' placeholder='Enter First Name'
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.firstName}
                onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="font-semibold text-sm">Last Name</label>
              <input type='text' placeholder='Enter Last Name'
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.lastName}
                onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                required
              />
            </div>
            <UsernameValidatedComponent
              userName={userData.userName}
              setUserName={(value) => setUserData({ ...userData, userName: value })}
              getUsername={getFamilyMemberUsername}
              className={{
                parentDiv: "",
                label: "font-semibold text-sm",
                input: "w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary",
              }}
              touched={usernameTouched}
              setTouched={setUsernameTouched}
              setIsChecking={setIsUsernameChecking}
              id={userData?.id}
              required
            />
            <div>
              <PasswordValidation
                password={password}
                setPassword={setPassword}
                isValid={isValidPassword}
                setIsValid={setIsValidPassword}
                className={{
                  label: "font-semibold text-sm",
                  div: "w-full text-sm relative",
                  input: "w-full rounded py-2.5 px-4 mt-2.5 border-2 text-sm text-black outline-secondary"
                }}
                touched={passwordTouched}
                setTouched={setPasswordTouched}                
              />
            </div>
            <EmailAlreadyExistChecking
              text={userData.emailAddress}
              setText={(value) => setUserData({ ...userData, emailAddress: value })}
              getEmailChecking={getFamilyMemberEmailChecking}
              className={{
                parentDiv: "",
                label: "font-semibold text-sm",
                input: "w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary",
              }}
              touched={emailTouched}
              setTouched={setEmailTouched}
              setIsChecking={setIsEmailChecking}
              id={userData?.id}
              required
            />
            <div>
              <label className="font-semibold text-sm">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formatUSPhone(userData.phoneNumber)}
                onChange={(e) => {
                  setPhoneTouched(true);
                  handleUSPhoneChange(e, (digits) =>
                    setUserData((prev: any) => ({
                      ...prev,
                      phoneNumber: digits,
                    }))
                  );
                }}                
                className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                placeholder="Phone No."
              />

              {!isPhoneValid && phoneTouched && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid 10-digit phone number
                </p>
              )}
            </div>

            <div>
              <label className="font-semibold text-sm">
                Birthday
              </label>

              <CustomFormatDatePicker
                value={userData.birthday}
                onChange={(val) =>
                  setUserData((prev: any) => ({
                    ...prev,
                    birthday: val,
                  }))
                }
                required={false}
              />
            </div>
            <div />

            <div >
              <FileDrop
                value={userData.filePath}
                setValue={(value) => setUserData({ ...userData, filePath: value })}
              />
            </div>
            <div />

            <div className="flex max-xm:flex-col gap-4 !mt-4">
              <button
                type="button"
                className="px-6 py-2 min-w-[150px] rounded text-black text-sm font-semibold border-none outline-none bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold border-none outline-none bg-secondary hover:bg-custom-blue"
                disabled={isUsernameChecking || isEmailChecking}
              >
                {
                  (isUsernameChecking || isEmailChecking || isProcessing)
                    ?
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18px" fill="#fff" className="mr-2 inline animate-spin"
                        viewBox="0 0 26.349 26.35">
                        <circle cx="13.792" cy="3.082" r="3.082" data-original="#000000" />
                        <circle cx="13.792" cy="24.501" r="1.849" data-original="#000000" />
                        <circle cx="6.219" cy="6.218" r="2.774" data-original="#000000" />
                        <circle cx="21.365" cy="21.363" r="1.541" data-original="#000000" />
                        <circle cx="3.082" cy="13.792" r="2.465" data-original="#000000" />
                        <circle cx="24.501" cy="13.791" r="1.232" data-original="#000000" />
                        <path
                          d="M4.694 19.84a2.155 2.155 0 0 0 0 3.05 2.155 2.155 0 0 0 3.05 0 2.155 2.155 0 0 0 0-3.05 2.146 2.146 0 0 0-3.05 0z"
                          data-original="#000000" />
                        <circle cx="21.364" cy="6.218" r=".924" data-original="#000000" />
                      </svg>
                      Processing...
                    </>
                    :
                    "Save"
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditFamilyMember